import { Divider, Grid } from "@mui/material";
import ChildCard from "@shared-layouts/ChildCard";
import ParentCard from "@shared-layouts/ParentCard";
import {
  CustomCardHeader,
  DividerWrapper,
} from "@src/components/shared-layouts/ChildCard/style";
import RichTextEditor from "@src/components/shared-ui-components/RichTextEditor";
import { useCallback, useEffect, useState } from "react";
import { Editor } from "@tiptap/react";
import RichTextActionBar from "@src/components/shared-ui-components/RichTextActionBar";
import LeavePageModal from "@src/components/shared-layouts/DefaultLayout/LeavePageModal";
import { useBlocker } from "react-router-dom";
import { TotalCardWrapper } from "../TotalSales/style";

import {
  NotesWrapper,
  EntryContainer,
  NewEntryContainer,
  ChildCardWrapper,
  StyledButton,
} from "./style";
import EmptyState from "./EmptyState";

interface NotesProps {
  title: string;
  data: noteSectionType[] | null;
  loading: boolean;
  viewMode: boolean;
  isMobile: boolean;
  notesSectionVisibility: string[];
  onNewNote: () => void;
  onNoteClick: () => void;
  onNoteUpdate: (content: string, sectionId: number, entryId: number) => void;
  onConfirmSendNotesEmails: () => void;
  onDoneClick: () => void;
}

const Notes = ({
  title,
  data,
  loading,
  viewMode,
  isMobile,
  notesSectionVisibility,
  onNewNote,
  onNoteClick,
  onNoteUpdate,
  onConfirmSendNotesEmails,
  onDoneClick,
}: NotesProps) => {
  const [editor, setEditor] = useState<Editor | null>(null);
  const [editors, setEditors] = useState<noteSectionType[]>([]);
  const [activeEditorsIds, setActiveEditorsIds] = useState<string[]>([]);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      activeEditorsIds.length > 0 &&
      currentLocation.pathname !== nextLocation.pathname
  );

  const areNoteContentsEmpty = (noteSections: noteSectionType[]): boolean => {
    if (noteSections.length === 0) return true;

    return noteSections.every(
      (section) =>
        section.entries.length === 0 ||
        section.entries.every((entry) => {
          const cleanContent = entry.content
            .replace(/<p><\/p>/g, "")
            .replace(/<p class="tiptap-paragraph"><\/p>/g, "")
            .replace(/<br\s*\/?>/g, "")
            .replace(/\s+/g, "")
            .trim();
          return cleanContent === "" || cleanContent === "<p></p>";
        })
    );
  };

  const handleConfirmNoteUpdate = useCallback(
    (content: string, sectionId: number, entryId: number, editorId: string) => {
      if (content !== "<p></p>") {
        onNoteUpdate(content, sectionId, entryId);
        setActiveEditorsIds((prev) => prev.filter((id) => id !== editorId));
      }
    },
    [onNoteUpdate]
  );

  const handleActiveEditor = (richTextEditor: Editor, editorId: string) => {
    if (richTextEditor) {
      if (!activeEditorsIds.includes(editorId)) {
        setActiveEditorsIds((prev) => [...prev, editorId]);
      }
      setEditor(richTextEditor);
    }
  };

  const getUserTimestampLabel = (
    actionBy: string,
    actionAt: string,
    actionType: string
  ) => {
    // Just use the timestamp string directly from the database
    return `${actionType} ${actionBy}, at ${actionAt}`;
  };

  const sectionVisible = (sectionTitle: string, entries: noteEntryType[]) => {
    return (
      !viewMode ||
      notesSectionVisibility.includes(sectionTitle) ||
      entries.length > 0
    );
  };

  const submitDoneClick = () => {
    setActiveEditorsIds([]);
    onDoneClick();
  };

  const handleDoneButton = () => {
    if (activeEditorsIds.length > 0) {
      setShowLeaveDialog(true);
      return;
    }

    submitDoneClick();
  };

  const removeFromEntryIds = (editorId: string) => {
    setActiveEditorsIds((prev) => prev.filter((id) => id !== editorId));
  };

  const onHandleLeaveCancel = () => {
    setShowLeaveDialog(false);
  };

  useEffect(() => {
    if (!data?.length) return;
    setEditors(data);
  }, [data]);

  // Reset all editor states when data changes (location/date change)
  useEffect(() => {
    setEditor(null);
    setActiveEditorsIds([]);
  }, [data]);

  useEffect(() => {
    if (viewMode) {
      setActiveEditorsIds([]);
    }
  }, [viewMode]);

  useEffect(() => {
    setShowLeaveDialog(blocker.state === "blocked");
  }, [blocker]);

  return (
    <TotalCardWrapper onClick={onNoteClick}>
      <ParentCard
        title={title}
        hideViewAllButton
        hidePrimaryButton={viewMode}
        handlePrimaryButton={handleDoneButton}
      >
        <NotesWrapper>
          {!data?.length ? (
            <EmptyState
              loading={loading}
              onClick={onNewNote}
              isMobile={isMobile}
            />
          ) : (
            <ChildCardWrapper>
              {!viewMode && <RichTextActionBar editor={editor} />}
              <ChildCard
                title=""
                disableDividerStyle
                hideDivider
                isNotesTable={!viewMode}
                viewMode={viewMode}
              >
                <Grid style={{ marginTop: 8 }} direction="column" container>
                  {editors.length > 0 &&
                    editors.map((section) => {
                      return sectionVisible(section.title, section.entries) ? (
                        <div key={`container-${section.id}`}>
                          <CustomCardHeader>{section.title}</CustomCardHeader>
                          <DividerWrapper>
                            <Divider />
                          </DividerWrapper>
                          {section.entries.map((entry: noteEntryType) => {
                            return (
                              <EntryContainer
                                key={`${entry.entryId} ${entry.sectionId}`}
                              >
                                <RichTextEditor
                                  initialHtml={entry.content}
                                  onConfirm={handleConfirmNoteUpdate}
                                  entryId={entry.entryId}
                                  loading={loading}
                                  sectionId={section.id}
                                  updatedLabel={
                                    entry.lastUpdatedBy && entry.lastUpdatedAt
                                      ? getUserTimestampLabel(
                                          entry.lastUpdatedBy,
                                          entry.lastUpdatedAt,
                                          "Updated by"
                                        )
                                      : getUserTimestampLabel(
                                          entry.createdBy,
                                          entry.createdAt,
                                          "Created by"
                                        )
                                  }
                                  onFocus={handleActiveEditor}
                                  onCancel={removeFromEntryIds}
                                  hasActiveEditors={activeEditorsIds.length > 0}
                                  viewMode={viewMode}
                                  placeholder={`Add ${section.title} Notes Here`}
                                  editorId={`${entry.entryId} ${entry.sectionId}`}
                                />
                              </EntryContainer>
                            );
                          })}
                          <NewEntryContainer>
                            <RichTextEditor
                              onConfirm={handleConfirmNoteUpdate}
                              entryId={0}
                              loading={loading}
                              sectionId={section.id}
                              onFocus={handleActiveEditor}
                              onCancel={removeFromEntryIds}
                              hasActiveEditors={activeEditorsIds.length > 0}
                              viewMode={viewMode}
                              placeholder={`Add ${section.title} Notes Here`}
                              editorId={`0 ${section.id}`}
                            />
                          </NewEntryContainer>
                        </div>
                      ) : null;
                    })}
                </Grid>
                <StyledButton
                  disabled={areNoteContentsEmpty(data)}
                  onClick={onConfirmSendNotesEmails}
                >
                  Send Email with notes
                </StyledButton>
              </ChildCard>
            </ChildCardWrapper>
          )}
        </NotesWrapper>
      </ParentCard>
      <LeavePageModal
        onClose={() => {
          if (blocker?.reset) {
            blocker.reset();
          }
          onHandleLeaveCancel();
        }}
        onConfirm={() => {
          if (blocker.state === "blocked") {
            blocker.proceed();
          }
          setActiveEditorsIds([]);
          onDoneClick();
          setShowLeaveDialog(false);
        }}
        open={showLeaveDialog}
      />
    </TotalCardWrapper>
  );
};

export default Notes;
